import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  Chip,
  Stack,
} from '@mui/material';
import {
  CheckCircle,
  CleaningServices,
  CalendarToday,
  Info,
} from '@mui/icons-material';
import { DESIGN_TOKENS } from '../../../design-system/tokens/colors';

export interface TeatCleaningData {
  api_status: string;
  api_message: string;
  operation_details: {
    handling_type_code: string;
    entity_type: string;
    identifier: string;
    handling_date: string;
    handling_type: string;
    details: string;
    records_processed: number;
  };
  entity_profile: {
    _key: string;
    _id: string;
    _rev: string;
    farm_id: string;
    current_farm_id?: string;
    birth_farm_id?: string;
    name: string;
    cattle_id?: {
      tag: string;
      country_code: string;
      manufacturer_code: number;
      unique_id: string;
    };
    cattle_tags?: string[];
    breed_code?: string;
    gender?: string;
    birth_date?: string;
    birth_weight_kg?: number;
    production_purpose?: string | null;
    dam_cattle_id?: string | null;
    sire_cattle_id?: string | null;
    current_paddock_id?: string;
    death_date?: string | null;
    death_reason?: string | null;
    current_status?: string;
    created_at: string;
    updated_at: string;
  };
}

export interface TeatCleaningDetailsData {
  data: TeatCleaningData;
  resumo: string;
  url: string;
}

export interface TeatCleaningDetailsViewProps {
  teatCleaningData: TeatCleaningDetailsData;
}

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('pt-BR');
};

const getStatusColor = (status: string) => {
  return status === 'ativo' ? DESIGN_TOKENS.colors.brand.primary : DESIGN_TOKENS.colors.status.error[500];
};

const getHandlingTypeLabel = (handlingType: string) => {
  switch (handlingType) {
    case 'limpeza_tetos':
      return 'Limpeza de Tetos';
    default:
      return handlingType;
  }
};

const TeatCleaningDetailsView: React.FC<TeatCleaningDetailsViewProps> = ({
  teatCleaningData,
}) => {
  const { data, resumo } = teatCleaningData;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, textAlign: 'center' }}>
        <Avatar
          sx={{
            width: 80,
            height: 80,
            bgcolor: DESIGN_TOKENS.colors.brand.primary,
            mx: 'auto',
            mb: 2,
            fontSize: '2rem',
          }}
        >
          <CleaningServices sx={{ fontSize: '2rem' }} />
        </Avatar>
        <Typography variant="h4" sx={{ fontWeight: 700, color: DESIGN_TOKENS.colors.brand.primary, mb: 1 }}>
          Detalhes do Manejo de Limpeza
        </Typography>
        <Typography variant="body1" sx={{ color: DESIGN_TOKENS.colors.text.secondary, mb: 2 }}>
          {resumo}
        </Typography>
      </Box>

      {/* Status Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircle sx={{ color: DESIGN_TOKENS.colors.brand.primary, fontSize: '1.5rem' }} />
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Status da Operação
                </Typography>
                <Chip
                  label="Completo"
                  sx={{
                    bgcolor: DESIGN_TOKENS.colors.brand.primary,
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.8rem',
                    '& .MuiChip-icon': {
                      color: 'white'
                    }
                  }}
                />
              </Box>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <img 
                src="/vaca.png" 
                alt="Animal" 
                style={{ width: 24, height: 24, filter: 'brightness(0) invert(1)' }}
              />
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Animal Tratado
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {data.entity_profile.name}
                </Typography>
              </Box>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Info sx={{ color: DESIGN_TOKENS.colors.status.info[500], fontSize: '1.5rem' }} />
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Registros Processados
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {data.operation_details.records_processed}
                </Typography>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Management Details */}
      <Box sx={{ display: 'flex', gap: 3, mb: 3, flexWrap: 'wrap' }}>
        {/* Management Information */}
        <Box sx={{ flex: '1 1 300px', minWidth: 300 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Avatar
                  sx={{
                    bgcolor: DESIGN_TOKENS.colors.brand.primary,
                    width: 40,
                    height: 40,
                  }}
                >
                  <CleaningServices />
                </Avatar>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: DESIGN_TOKENS.colors.text.primary,
                  }}
                >
                  Informações do Manejo
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                <Box sx={{ minWidth: 200 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Tipo de Manejo
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: DESIGN_TOKENS.colors.brand.primary }}>
                    {getHandlingTypeLabel(data.operation_details.handling_type)}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 200 }}>
                  <CalendarToday sx={{ color: DESIGN_TOKENS.colors.text.secondary, fontSize: '1.2rem' }} />
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                      Data do Manejo
                    </Typography>
                    <Typography variant="body1">
                      {formatDate(data.operation_details.handling_date)}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ minWidth: 150 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Animal Identificado
                  </Typography>
                  <Typography variant="body1">
                    {data.operation_details.identifier}
                  </Typography>
                </Box>

                <Box sx={{ minWidth: 200 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Código da Operação
                  </Typography>
                  <Typography variant="body1" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                    {data.operation_details.handling_type_code}
                  </Typography>
                </Box>

                {data.operation_details.details && (
                  <Box sx={{ minWidth: 250 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                      Detalhes Adicionais
                    </Typography>
                    <Typography variant="body1">
                      {data.operation_details.details}
                    </Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Animal Information */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: DESIGN_TOKENS.colors.text.primary }}>
            Informações do Animal
          </Typography>
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            <Box sx={{ flex: '1 1 300px' }}>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Nome do Animal
                  </Typography>
                  <Typography variant="body1">
                    {data.entity_profile.name}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                    ID do Animal
                  </Typography>
                  <Typography variant="body1" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                    {data.entity_profile._key}
                  </Typography>
                </Box>

                {data.entity_profile.cattle_id && (
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                      Tag RFID
                    </Typography>
                    <Typography variant="body1" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                      {data.entity_profile.cattle_id.tag}
                    </Typography>
                  </Box>
                )}

                {data.entity_profile.cattle_tags && (
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                      Animais do Lote
                    </Typography>
                    <Typography variant="body1">
                      {data.entity_profile.cattle_tags.join(', ')}
                    </Typography>
                  </Box>
                )}

                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Raça
                  </Typography>
                  <Typography variant="body1" sx={{ color: DESIGN_TOKENS.colors.brand.primary, fontWeight: 600 }}>
                    {data.entity_profile.breed_code}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Gênero
                  </Typography>
                  <Typography variant="body1" sx={{ color: DESIGN_TOKENS.colors.brand.primary, fontWeight: 600 }}>
                    {data.entity_profile.gender}
                  </Typography>
                </Box>
              </Stack>
            </Box>
            
            <Box sx={{ flex: '1 1 300px' }}>
              <Stack spacing={2}>
                {data.entity_profile.current_status && (
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                      Status Atual
                    </Typography>
                    <Chip
                      label={data.entity_profile.current_status}
                      size="small"
                      sx={{
                        bgcolor: getStatusColor(data.entity_profile.current_status),
                        color: 'white',
                        fontWeight: 600,
                        '& .MuiChip-icon': {
                          color: 'white'
                        }
                      }}
                    />
                  </Box>
                )}

                {data.entity_profile.birth_date && (
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                      Data de Nascimento
                    </Typography>
                    <Typography variant="body1">
                      {formatDate(data.entity_profile.birth_date)}
                    </Typography>
                  </Box>
                )}

                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Peso ao Nascer
                  </Typography>
                  <Typography variant="body1">
                    {data.entity_profile.birth_weight_kg} kg
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Pasto Atual
                  </Typography>
                  <Typography variant="body1">
                    Pasto {data.entity_profile.current_paddock_id}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Última Atualização
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(data.entity_profile.updated_at)}
                  </Typography>
                </Box>
              </Stack>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default TeatCleaningDetailsView;